import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import {
  ButtonComponent,
  BlockDirective,
  HoldingResponse,
  IfFeatureService,
  ToastMessageService,
  ModalCloseDirective,
  ModalPanelComponent,
  NotificationComponent,
  SymbolLogoComponent,
  TickerSymbolComponent,
  TickerSymbolTableContainerComponent,
  DataAnalyticsService,
  MediaQueryService,
  Breakpoint,
  BreakpointState,
  ModalHeaderComponent,
  ModalHeaderTitleComponent
} from '@tdwb/core';
import { FplOnLoanHolding } from '@tdwb/webbroker/core';
import { FplRestService } from './../services/fpl-rest.service';

@Component({
  selector: 'td-wb-fpl-recall-security',
  templateUrl: 'fpl-recall-security.component.html',
  styleUrls: ['fpl-recall-security.component.scss'],
  imports: [
    TranslateDirective,
    ModalPanelComponent,
    SymbolLogoComponent,
    NotificationComponent,
    ButtonComponent,
    ModalCloseDirective,
    BlockDirective,
    TickerSymbolComponent,
    TickerSymbolTableContainerComponent,
    ModalHeaderComponent,
    ModalHeaderTitleComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FplRecallSecurityComponent implements OnDestroy {
  @Input() holding: HoldingResponse | FplOnLoanHolding;
  @Input() accountId: string;
  @Input() numberOfShares: number;
  @Output() recallSucessful: EventEmitter<void> = new EventEmitter<void>();

  showLogo: boolean = false;
  disableRecall: boolean = false;
  isMobile: boolean = false;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(private fplRestService: FplRestService,
              private toastMessageService: ToastMessageService,
              private changeDetectorRef: ChangeDetectorRef,
              private ifFeatureService: IfFeatureService,
              private dataAnalyticsService: DataAnalyticsService,
              private mediaQueryService: MediaQueryService
  ) {
    this.mediaQueryService.observe(Breakpoint.SM)
      .pipe(takeUntilDestroyed())
      .subscribe((breakpointState: BreakpointState): void => {
        this.isMobile = breakpointState.isBreakpointMatch(Breakpoint.SM);
        this.changeDetectorRef.markForCheck();
      });
    this.showLogo = this.ifFeatureService.getFeatureFlag('wbSymbolLogoOnHoldingsEnabled');
  }

  cancelSecurityRecall(): void {
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:recall:no');
  }

  handleSecurityRecall(): void {
    this.disableRecall = true;
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:recall:yes');
    this.fplRestService.createRestrictions({
      accountId: this.accountId,
      restrictionType: 'SECURITY',
      securities: [this.holding.cusip],
      recall: true
    })
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (response: boolean): void => {
          this.disableRecall = false;
          if (response) {
            if (this.holding.symbol?.tickerSymbol) {
              this.toastMessageService.setToast('tdWb.fpl.recall.TOAST_MESSAGE_WITH_SYMBOL', { numberOfShares : this.numberOfShares, symbol: this.holding.symbol.tickerSymbol });
            } else {
              this.toastMessageService.setToast('tdWb.fpl.recall.TOAST_MESSAGE_WITHOUT_SYMBOL', { numberOfShares : this.numberOfShares });
            }
            this.recallSucessful.emit();
          }
          this.changeDetectorRef.markForCheck();
        },
        error: (): void => {
          this.disableRecall = false;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }
}
